import polylabel from "polylabel";

export const dedupeFeaturesById = (features) => {
  const seen = {};
  return features.filter((feature) => {
    if (seen[feature.id]) return false;
    seen[feature.id] = true;
    return true;
  });
};

/** @typedef {[number, number]} Position */

/**
 * @param {any} coords
 * @param {number} dLng
 * @param {number} dLat
 * @returns {any}
 */
export const translateCoordinates = (coords, dLng, dLat) => {
  if (typeof coords[0] === "number") {
    return [coords[0] + dLng, coords[1] + dLat];
  }
  return coords.map((c) => translateCoordinates(c, dLng, dLat));
};

/**
 * @param {import("geojson").Geometry} geometry
 * @param {number} dLng
 * @param {number} dLat
 */
export const translateGeometry = (geometry, dLng, dLat) => ({
  ...geometry,
  coordinates: translateCoordinates(geometry.coordinates, dLng, dLat),
});

/**
 * @param {import("geojson").Geometry} geometry
 * @returns {{ coordinate: Position, path: number[] }[]}
 */
export const verticesOf = (geometry) => {
  switch (geometry.type) {
    case "LineString":
      return geometry.coordinates.map((coordinate, i) => ({
        coordinate,
        path: [i],
      }));
    case "Polygon":
      return geometry.coordinates.flatMap((ring, ringIdx) =>
        ring.slice(0, -1).map((coordinate, i) => ({
          coordinate,
          path: [ringIdx, i],
        }))
      );
    case "MultiLineString":
      return geometry.coordinates.flatMap((line, lineIdx) =>
        line.map((coordinate, i) => ({
          coordinate,
          path: [lineIdx, i],
        }))
      );
    case "MultiPolygon":
      return geometry.coordinates.flatMap((polygon, polyIdx) =>
        polygon.flatMap((ring, ringIdx) =>
          ring.slice(0, -1).map((coordinate, i) => ({
            coordinate,
            path: [polyIdx, ringIdx, i],
          }))
        )
      );
    default:
      return [];
  }
};

/**
 * @param {Position[]} coordinates
 * @param {number[]} pathPrefix
 * @returns {{ coordinate: Position, insertPath: number[] }[]}
 */
const midpointsOfLine = (coordinates, pathPrefix) =>
  coordinates.slice(0, -1).map((coordinate, i) => {
    const nextCoordinate = coordinates[i + 1];
    return {
      coordinate: [
        (coordinate[0] + nextCoordinate[0]) / 2,
        (coordinate[1] + nextCoordinate[1]) / 2,
      ],
      insertPath: [...pathPrefix, i + 1],
    };
  });

/**
 * @param {import("geojson").Geometry} geometry
 * @returns {{ coordinate: Position, insertPath: number[] }[]}
 */
export const midpointsOf = (geometry) => {
  switch (geometry.type) {
    case "LineString":
      return midpointsOfLine(geometry.coordinates, []);
    case "Polygon":
      return geometry.coordinates.flatMap((ring, ringIdx) =>
        midpointsOfLine(ring, [ringIdx])
      );
    case "MultiLineString":
      return geometry.coordinates.flatMap((line, lineIdx) =>
        midpointsOfLine(line, [lineIdx])
      );
    case "MultiPolygon":
      return geometry.coordinates.flatMap((polygon, polyIdx) =>
        polygon.flatMap((ring, ringIdx) =>
          midpointsOfLine(ring, [polyIdx, ringIdx])
        )
      );
    default:
      return [];
  }
};

/**
 * @param {import("geojson").Geometry} geometry
 * @param {number[]} path
 */
const classifyPath = (geometry, path) => ({
  isLine:
    (geometry.type === "LineString" && path.length === 1) ||
    (geometry.type === "MultiLineString" && path.length === 2),
  isPolygonRing:
    (geometry.type === "Polygon" && path.length === 2) ||
    (geometry.type === "MultiPolygon" && path.length === 3),
});

/** Stable string key for a vertex/midpoint path (for equality and feature props). */
export const pathKey = (path) => JSON.stringify(path);

/**
 * Deep-clone coordinates and walk `path` to the mutable target array.
 * Returns null when any intermediate path index is missing.
 * @param {import("geojson").Geometry} geometry
 * @param {number[]} path
 */
const resolveTarget = (geometry, path) => {
  const newCoords = JSON.parse(JSON.stringify(geometry.coordinates));
  let target = newCoords;
  for (let i = 0; i < path.length - 1; i++) {
    target = target[path[i]];
    if (target == null) return null;
  }
  return { newCoords, target, lastIdx: path[path.length - 1] };
};

/**
 * @param {import("geojson").Geometry} geometry
 * @param {number[]} path
 * @param {Position} position
 */
export const moveVertex = (geometry, path, position) => {
  const resolved = resolveTarget(geometry, path);
  if (!resolved) return geometry;
  const { newCoords, target, lastIdx } = resolved;
  target[lastIdx] = [position[0], position[1]];

  const { isPolygonRing } = classifyPath(geometry, path);

  if (isPolygonRing && lastIdx === 0) {
    target[target.length - 1] = [position[0], position[1]];
  }

  return { ...geometry, coordinates: newCoords };
};

/**
 * @param {import("geojson").Geometry} geometry
 * @param {number[]} insertPath
 * @param {Position} position
 */
export const insertVertex = (geometry, insertPath, position) => {
  const resolved = resolveTarget(geometry, insertPath);
  if (!resolved) return geometry;
  const { newCoords, target, lastIdx } = resolved;
  target.splice(lastIdx, 0, [position[0], position[1]]);

  return { ...geometry, coordinates: newCoords };
};

/**
 * @param {import("geojson").Geometry} geometry
 * @param {number[]} path
 * @returns {import("geojson").Geometry | null}
 */
export const removeVertex = (geometry, path) => {
  const resolved = resolveTarget(geometry, path);
  if (!resolved) return null;
  const { newCoords, target, lastIdx } = resolved;

  const { isLine, isPolygonRing } = classifyPath(geometry, path);
  if (!isLine && !isPolygonRing) return null;

  const maxIdx = isPolygonRing ? target.length - 2 : target.length - 1;
  if (lastIdx < 0 || lastIdx > maxIdx) return null;

  if ((isLine && target.length <= 2) || (isPolygonRing && target.length <= 4)) {
    return null;
  }

  target.splice(lastIdx, 1);

  if (isPolygonRing && lastIdx === 0) {
    target[target.length - 1] = [target[0][0], target[0][1]];
  }

  return { ...geometry, coordinates: newCoords };
};

/**
 * @param {import("geojson").Geometry} geometry
 * @param {number[] | null} [selectedPath]
 * @param {number[] | null} [hoveredPath]
 */
export const vertexFeatureCollection = (
  geometry,
  selectedPath = null,
  hoveredPath = null
) => {
  const selectedPathJson = selectedPath && pathKey(selectedPath);
  const hoveredPathJson = hoveredPath && pathKey(hoveredPath);
  return {
    type: "FeatureCollection",
    features: verticesOf(geometry).map(({ coordinate, path }) => {
      const pathJson = pathKey(path);
      return {
        type: "Feature",
        geometry: { type: "Point", coordinates: coordinate },
        properties: {
          path: pathJson,
          ...(selectedPathJson === pathJson ? { selected: true } : {}),
          ...(hoveredPathJson === pathJson ? { hovered: true } : {}),
        },
      };
    }),
  };
};

/** @param {import("geojson").Geometry} geometry */
export const midpointFeatureCollection = (geometry) => ({
  type: "FeatureCollection",
  features: midpointsOf(geometry).map(({ coordinate, insertPath }) => ({
    type: "Feature",
    geometry: { type: "Point", coordinates: coordinate },
    properties: { path: pathKey(insertPath) },
  })),
});

export const mainFeatureOf = (geometry, id) => ({
  type: "Feature",
  geometry,
  properties: {},
  id,
});

export const centerFeatureOf = (geometry, id, label) => {
  const [lng, lat] = centerOf(geometry);
  return {
    type: "Feature",
    id,
    geometry: { type: "Point", coordinates: [lng, lat] },
    properties: { label },
  };
};

/** @param {import("geojson").Geometry} geometry @returns {Position} */
export const centerOf = (geometry) => {
  if (geometry.type === "Point") {
    return geometry.coordinates;
  }

  const coordinates =
    geometry.type === "LineString"
      ? [[...geometry.coordinates, geometry.coordinates[0]]]
      : geometry.coordinates;

  return polylabel(coordinates, 1.0);
};
